import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Box, Grid, Typography, Paper, Skeleton, Chip, Button, Avatar,
  LinearProgress, List, ListItem, ListItemAvatar, ListItemText,
  Divider, Alert, Stack, Tooltip
} from '@mui/material';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import { useNextGenAuth } from '../context/NextGenAuthContext';
import { useNexusBilling } from '../context/NexusBillingContext';
import { FEATURE_META } from '../api/billingApi';
import API_BASE_URL from '../../Config/config';

const CARD_BG = '#FFFFFF';
const BORDER = '1px solid #E5E7EB';

/* ── Metric Card ── */
const MetricCard = ({ icon, label, value, subtitle, color = '#6C3BFF', loading }) => (
  <Paper
    elevation={0}
    component={motion.div}
    whileHover={{ y: -3, boxShadow: `0 12px 32px ${color}22` }}
    transition={{ duration: 0.2 }}
    sx={{
      p: 2.5, borderRadius: '16px',
      background: CARD_BG,
      border: BORDER,
      position: 'relative', overflow: 'hidden',
      cursor: 'default',
      '&::before': {
        content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${color} 0%, ${color}55 100%)`,
        borderRadius: '16px 16px 0 0',
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
      <Box
        sx={{
          p: 1.1, borderRadius: '10px',
          background: `${color}14`,
          border: `1px solid ${color}28`,
          color, display: 'flex', alignItems: 'center',
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 22 } })}
      </Box>
    </Box>
    {loading ? (
      <Stack spacing={0.75}>
        <Skeleton variant="text" width="55%" height={42} />
        <Skeleton variant="text" width="75%" height={20} />
        <Skeleton variant="text" width="60%" height={16} />
      </Stack>
    ) : (
      <>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1.1, mb: 0.4, letterSpacing: '-0.02em' }}>
          {value}
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500, fontSize: '0.82rem' }}>
          {label}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </>
    )}
  </Paper>
);

/* ── Section Card ── */
const SectionCard = ({ title, icon, children, action, loading, minHeight }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: '16px', height: '100%',
      background: CARD_BG, border: BORDER,
      minHeight,
      display: 'flex', flexDirection: 'column',
    }}
  >
    <Box
      sx={{
        px: 2.5, pt: 2.25, pb: 1.75,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ color: '#9B6FFF', display: 'flex' }}>
          {React.cloneElement(icon, { sx: { fontSize: 20 } })}
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F172A', fontSize: '0.9rem' }}>
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
    <Divider sx={{ mx: 2.5 }} />
    <Box sx={{ flex: 1, p: 2.5, pt: 2 }}>
      {loading ? (
        <Stack spacing={1.5}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={36} sx={{ borderRadius: 2 }} />
          ))}
        </Stack>
      ) : children}
    </Box>
  </Paper>
);

/* ── Empty State ── */
const EmptyState = ({ icon, title, message, action }) => (
  <Box sx={{ py: 3, textAlign: 'center' }}>
    <Box
      sx={{
        width: 52, height: 52, borderRadius: '14px', mx: 'auto', mb: 1.5,
        background: 'rgba(108,59,255,0.08)',
        border: '1px solid rgba(108,59,255,0.15)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#9B6FFF',
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: 24 } })}
    </Box>
    <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 600, mb: 0.5 }}>
      {title}
    </Typography>
    <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block' }}>
      {message}
    </Typography>
    {action && <Box sx={{ mt: 2 }}>{action}</Box>}
  </Box>
);

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] },
};

const stagger = {
  animate: { transition: { staggerChildren: 0.06 } },
};

/* ─────────── Feature allowances (TSG Nexus feature billing) ───────────
 * Every feature ships a free allowance; paid credits top it up. All the
 * numbers below come from GET /billing/summary via NexusBillingContext —
 * nothing here is computed or assumed on the client.
 */

const ALLOWANCE_ICONS = {
  AI_TUTOR: <PsychologyRoundedIcon />,
  MOCK_INTERVIEW: <RecordVoiceOverRoundedIcon />,
  ELITE_MASTERY: <AutoStoriesRoundedIcon />,
};

const formatAllowanceDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const AllowanceRow = ({ label, value, tone }) => (
  <Box
    sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
      px: 1.25, py: 0.75, borderRadius: '10px',
      background: '#F8FAFC', border: '1px solid #EEF0F3',
    }}
  >
    <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500, fontSize: '0.8rem', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 800, fontSize: '0.8rem', flexShrink: 0, color: tone === 'out' ? '#B91C1C' : tone === 'warn' ? '#B45309' : '#0F172A' }}
    >
      {value}
    </Typography>
  </Box>
);

const FeatureAllowanceCard = ({ code, feature, rows, headline, footnote, primaryAction, secondaryAction }) => {
  const meta = FEATURE_META[code] || {};
  const color = meta.color || '#6C3BFF';
  const legacyTill = feature && feature.legacyAccess ? feature.legacyAccess.validTill : null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5, borderRadius: '16px', height: '100%',
        background: CARD_BG, border: BORDER,
        display: 'flex', flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
        <Box
          sx={{
            width: 38, height: 38, borderRadius: '11px', flexShrink: 0,
            background: `${color}14`, border: `1px solid ${color}28`, color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {React.cloneElement(ALLOWANCE_ICONS[code] || <BoltRoundedIcon />, { sx: { fontSize: 20 } })}
        </Box>
        <Typography sx={{ fontWeight: 800, color: '#0F172A', fontSize: '0.95rem' }}>
          {(feature && feature.name) || meta.label || code}
        </Typography>
      </Box>

      {legacyTill && (
        <Chip
          size="small"
          label={`Included in your plan until ${formatAllowanceDate(legacyTill)}`}
          sx={{
            alignSelf: 'flex-start', mb: 1.25, height: 22, fontWeight: 700, fontSize: '0.68rem',
            bgcolor: 'rgba(6,182,212,0.12)', color: '#0E7490', border: '1px solid rgba(6,182,212,0.26)',
          }}
        />
      )}

      {headline && (
        <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600, fontSize: '0.82rem', mb: 1.25 }}>
          {headline}
        </Typography>
      )}

      <Stack spacing={0.65} sx={{ mb: 1.25 }}>
        {rows.map((r) => (
          <AllowanceRow key={r.key} label={r.label} value={r.value} tone={r.tone} />
        ))}
      </Stack>

      {footnote && (
        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, display: 'block', mb: 1.25 }}>
          {footnote}
        </Typography>
      )}

      <Box sx={{ mt: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {primaryAction}
        {secondaryAction}
      </Box>
    </Paper>
  );
};

const NextGenDashboard = () => {
  const { nextgen_builder } = useNextGenAuth();
  const { features: nexusFeatures, catalog: nexusCatalog } = useNexusBilling();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ghInvite, setGhInvite] = useState(null);
  const [versionCards, setVersionCards] = useState([]);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/dashboard`);
        setData(res.data.data);
      } catch (err) {
        // "Builder session required" is the backend's internal wording for
        // a request that arrived without an identity header. Rendering it
        // verbatim makes a plain logged-out state look like a system fault.
        // The axios response interceptor already retries once with the
        // stored builder identity and, only if that also fails, clears the
        // session and dispatches `nextgen-builder:session-expired` — which
        // NextGenLayout listens for and answers with the proper "log in
        // again" modal. All this has to do is not contradict that modal
        // with raw internal text sitting behind it.
        const isExpiredSession =
          err.response?.status === 400 &&
          err.response?.data?.message === 'Builder session required';
        setError(
          isExpiredSession
            ? 'Your session has expired. Please log in again to continue.'
            : (err.response?.data?.message || 'Failed to load dashboard')
        );
      } finally {
        setLoading(false);
      }
    };
    // GitHub access status — the backend live-syncs with GitHub on this call,
    // so acceptance is picked up automatically (no manual refresh needed).
    const fetchGithubInvite = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/github/my-invitation`);
        if (res.data.success && res.data.data) setGhInvite(res.data.data);
      } catch (err) {
        console.error('[Dashboard] GitHub invitation status fetch failed:', err);
      }
    };
    // One card per enrolled project version (subscription-aware)
    const fetchVersionCards = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/dashboard/projects`);
        if (res.data.success && Array.isArray(res.data.data)) setVersionCards(res.data.data);
      } catch (err) {
        console.error('[Dashboard] Version cards fetch failed:', err);
      }
    };
    fetchDashboard();
    fetchGithubInvite();
    fetchVersionCards();
  }, []);

  const versionProgressPct = (s) => (
    s === 'COMPLETED' ? 100 : s === 'UNDER_REVIEW' ? 80 : s === 'IN_PROGRESS' ? 50 : s === 'ACTIVE' ? 10 : 0
  );

  const referrals = data?.referrals || {};
  const wallet = data?.wallet || {};
  const subscription = data?.subscription || {};

  const firstName = data?.profile?.fullName?.split(' ')[0] || nextgen_builder?.fullName?.split(' ')[0] || 'Builder';

  /* ── Feature allowances (free balance + paid credits, server-provided) ── */
  const aiTutor = nexusFeatures ? nexusFeatures.AI_TUTOR : null;
  const mockInterview = nexusFeatures ? nexusFeatures.MOCK_INTERVIEW : null;
  const eliteMastery = nexusFeatures ? nexusFeatures.ELITE_MASTERY : null;

  const allowanceTone = (remaining) => (remaining === 0 ? 'out' : remaining <= 2 ? 'warn' : 'ok');

  const technologyTitle = (slug) => {
    const list = nexusCatalog && Array.isArray(nexusCatalog.technologies) ? nexusCatalog.technologies : [];
    const t = list.find((x) => x.slug === slug);
    return t ? (t.title || slug) : slug;
  };

  const allowanceBtnSx = {
    borderRadius: '10px', fontWeight: 700, textTransform: 'none', fontSize: '0.78rem',
    background: 'linear-gradient(135deg, #6C3BFF, #8B5CF6)', color: '#FFFFFF', px: 1.75,
    '&:hover': { background: 'linear-gradient(135deg, #5020E0, #7C3AED)' },
  };
  const allowanceGhostBtnSx = {
    borderRadius: '10px', fontWeight: 700, textTransform: 'none', fontSize: '0.78rem',
    color: '#6C3BFF', border: '1px solid rgba(108,59,255,0.3)', px: 1.75,
    '&:hover': { bgcolor: 'rgba(108,59,255,0.06)' },
  };

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <motion.div {...fadeUp}>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1440, mx: 'auto' }}>

        {/* ── Header ── */}
        <Box sx={{ mb: 3.5 }}>
          {loading ? (
            <Stack spacing={0.75}>
              <Skeleton variant="text" width={280} height={44} />
              <Skeleton variant="text" width={180} height={24} />
            </Stack>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, color: '#0F172A', mb: 0.5, letterSpacing: '-0.02em' }}
                >
                  {getGreeting()}, {firstName} 👋
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500 }}>
                    NextGen Builder Program
                  </Typography>
                  {subscription.isActive ? (
                    <Chip
                      label={`${subscription.plan?.toUpperCase()} · Active`}
                      size="small"
                      icon={<CheckCircleRoundedIcon sx={{ fontSize: '13px !important' }} />}
                      sx={{
                        bgcolor: 'rgba(34,197,94,0.12)',
                        color: '#22C55E',
                        border: '1px solid rgba(34,197,94,0.25)',
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        height: 24,
                      }}
                    />
                  ) : (
                    <Chip
                      label="Free tier · pay per feature"
                      size="small"
                      icon={<BoltRoundedIcon sx={{ fontSize: '13px !important' }} />}
                      onClick={() => navigate('/nextgen-builder/plans')}
                      sx={{
                        bgcolor: 'rgba(108,59,255,0.1)',
                        color: '#6C3BFF',
                        border: '1px solid rgba(108,59,255,0.22)',
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        height: 24,
                        cursor: 'pointer',
                        '& .MuiChip-icon': { color: '#6C3BFF' },
                      }}
                    />
                  )}
                </Box>
              </Box>
              {!loading && !subscription.isActive && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ShoppingCartRoundedIcon />}
                  onClick={() => navigate('/nextgen-builder/purchase')}
                  sx={{
                    background: 'linear-gradient(135deg, #6C3BFF, #8B5CF6)',
                    borderRadius: '10px',
                    fontWeight: 700,
                    px: 2,
                    '&:hover': { background: 'linear-gradient(135deg, #5020E0, #7C3AED)' },
                  }}
                >
                  Subscribe Now
                </Button>
              )}
            </Box>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* ── Your Feature Allowances ── */}
        {nexusFeatures && (
          <Box sx={{ mb: 3.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>Your feature allowances</Typography>
              <Button
                size="small"
                endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: '14px !important' }} />}
                onClick={() => navigate('/nextgen-builder/plans')}
                sx={{ color: '#6C3BFF', fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: 'rgba(108,59,255,0.08)' } }}
              >
                Manage credits
              </Button>
            </Box>

            <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, alignItems: 'stretch' }}>
              {aiTutor && (
                <FeatureAllowanceCard
                  code="AI_TUTOR"
                  feature={aiTutor}
                  headline={aiTutor.monthlyFree
                    ? `${aiTutor.monthlyFree.perScope} free ${aiTutor.unitLabelPlural} per tool every month`
                    : null}
                  rows={(aiTutor.monthlyFree ? aiTutor.monthlyFree.buckets : []).map((b) => ({
                    key: b.key || b.subFeature,
                    label: b.name || b.subFeature,
                    value: `${b.remaining} / ${b.total}`,
                    tone: allowanceTone(b.remaining),
                  }))}
                  footnote={aiTutor.paid ? `${aiTutor.paid.remaining} paid ${aiTutor.unitLabelPlural}` : null}
                  primaryAction={
                    <Button size="small" variant="contained" disableElevation onClick={() => navigate('/nextgen-builder/plans?feature=AI_TUTOR')} sx={allowanceBtnSx}>
                      Recharge AI Tutor
                    </Button>
                  }
                />
              )}

              {mockInterview && (
                <FeatureAllowanceCard
                  code="MOCK_INTERVIEW"
                  feature={mockInterview}
                  headline={mockInterview.monthlyFree && mockInterview.monthlyFree.buckets[0]
                    ? `${mockInterview.monthlyFree.buckets[0].remaining} / ${mockInterview.monthlyFree.buckets[0].total} free ${mockInterview.unitLabelPlural} this month`
                    : null}
                  rows={mockInterview.paid ? [{
                    key: 'paid',
                    label: `Paid ${mockInterview.unitLabelPlural}`,
                    value: `${mockInterview.paid.remaining}`,
                    tone: allowanceTone(mockInterview.paid.remaining),
                  }] : []}
                  footnote={`Free ${mockInterview.unitLabelPlural} reset on the 1st of every month. Paid ${mockInterview.unitLabelPlural} never expire.`}
                  primaryAction={
                    <Button size="small" variant="contained" disableElevation onClick={() => navigate('/nextgen-builder/plans?feature=MOCK_INTERVIEW')} sx={allowanceBtnSx}>
                      Get more interviews
                    </Button>
                  }
                />
              )}

              {eliteMastery && (
                <FeatureAllowanceCard
                  code="ELITE_MASTERY"
                  feature={eliteMastery}
                  headline={eliteMastery.freeAccess && eliteMastery.freeAccess.mode === 'PERCENT'
                    ? `First ${eliteMastery.freeAccess.percent}% of every technology's ${eliteMastery.unitLabelPlural} are free`
                    : eliteMastery.freeAllowance && eliteMastery.freeAllowance.perScope != null
                      ? `First ${eliteMastery.freeAllowance.perScope} ${eliteMastery.unitLabelPlural} of every technology are free`
                      : null}
                  rows={[
                    ...((eliteMastery.premium && Array.isArray(eliteMastery.premium.byTechnology)) ? eliteMastery.premium.byTechnology : []).map((t) => ({
                      key: `premium-${t.technology}`,
                      label: technologyTitle(t.technology),
                      value: t.active
                        ? `Full access until ${new Date(t.expiresAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`
                        : `Access ended ${new Date(t.expiresAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`,
                      tone: t.active ? (t.daysRemaining != null && t.daysRemaining <= 14 ? 'warn' : 'ok') : 'out',
                    })),
                    ...((eliteMastery.paid && Array.isArray(eliteMastery.paid.byTechnology)) ? eliteMastery.paid.byTechnology : []).filter((t) => t.total > 0).map((t) => ({
                      key: t.technology,
                      label: technologyTitle(t.technology),
                      value: `${t.remaining} ${eliteMastery.unitLabelPlural} left`,
                      tone: allowanceTone(t.remaining),
                    })),
                  ]}
                  footnote={(eliteMastery.premium && eliteMastery.premium.byTechnology && eliteMastery.premium.byTechnology.some((t) => t.active))
                    ? 'Full access covers every page of the technology, including new ones, until it expires.'
                    : 'No premium access yet'}
                  primaryAction={
                    <Button size="small" variant="contained" disableElevation onClick={() => navigate('/nextgen-builder/elite-mastery')} sx={allowanceBtnSx}>
                      Explore Elite Mastery
                    </Button>
                  }
                  secondaryAction={
                    <Button size="small" onClick={() => navigate('/nextgen-builder/plans?feature=ELITE_MASTERY')} sx={allowanceGhostBtnSx}>
                      Unlock a technology
                    </Button>
                  }
                />
              )}
            </Box>
          </Box>
        )}

        {/* ── GitHub Access Status ── */}
        {ghInvite && (
          <Box sx={{
            mb: 3, p: 2.5, borderRadius: '16px',
            bgcolor: ghInvite.status === 'ACCEPTED' ? 'rgba(34,197,94,0.06)'
              : ghInvite.status === 'INVITED' ? 'rgba(245,158,11,0.06)'
              : 'rgba(239,68,68,0.05)',
            border: '1px solid',
            borderColor: ghInvite.status === 'ACCEPTED' ? 'rgba(34,197,94,0.25)'
              : ghInvite.status === 'INVITED' ? 'rgba(245,158,11,0.3)'
              : 'rgba(239,68,68,0.22)',
            display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'
          }}>
            <Typography sx={{ fontSize: '1.4rem', lineHeight: 1 }}>
              {ghInvite.status === 'ACCEPTED' ? '🟢' : ghInvite.status === 'INVITED' ? '🟡' : '🔴'}
            </Typography>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0F172A' }}>
                {ghInvite.status === 'ACCEPTED' ? 'GitHub Collaborator Active'
                  : ghInvite.status === 'INVITED' ? 'GitHub Invitation Pending'
                  : 'GitHub Invitation Failed'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B' }}>
                {ghInvite.repository_owner && ghInvite.repository_name
                  ? `${ghInvite.repository_owner}/${ghInvite.repository_name} · ` : ''}
                @{ghInvite.github_username}
                {ghInvite.requested_at ? ` · Invited ${new Date(ghInvite.requested_at).toLocaleDateString()}` : ''}
                {ghInvite.accepted_at ? ` · Accepted ${new Date(ghInvite.accepted_at).toLocaleDateString()}` : ''}
              </Typography>
              {ghInvite.status === 'INVITED' && (
                <Typography variant="caption" sx={{ display: 'block', color: '#B45309', fontWeight: 600 }}>
                  Accept your GitHub invitation (check your email) to start development on the repository.
                </Typography>
              )}
              {['FAILED', 'EXPIRED'].includes(ghInvite.status) && ghInvite.failed_reason && (
                <Typography variant="caption" sx={{ display: 'block', color: '#B91C1C', fontWeight: 600 }}>
                  {ghInvite.failed_reason}
                </Typography>
              )}
            </Box>
            {ghInvite.status === 'ACCEPTED' && ghInvite.repository_owner && (
              <Button size="small" variant="outlined" component="a" target="_blank"
                href={`https://github.com/${ghInvite.repository_owner}/${ghInvite.repository_name}`}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
                Open Repository
              </Button>
            )}
            {['FAILED', 'EXPIRED'].includes(ghInvite.status) && (
              <Button size="small" variant="outlined" color="error"
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API_BASE_URL}/api/nextgen-builder/github/invitations/retry`, { invitationId: ghInvite.id });
                    if (res.data.success) setGhInvite(prev => ({ ...prev, ...(res.data.data.record || {}), status: res.data.data.status, failed_reason: res.data.data.reason || null }));
                  } catch (e) { console.error('[Dashboard] Retry failed:', e); }
                }}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
                Retry Invitation
              </Button>
            )}
          </Box>
        )}

        {/* ── My Project Versions (one card per enrolled version) ── */}
        {versionCards.length > 0 && (
          <Box sx={{ mb: 3.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>My Project Versions</Typography>
              {/* Progression indicator: V1 → V2 → V3 → V4 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                {versionCards.map((v, i) => (
                  <React.Fragment key={v.id}>
                    <Chip
                      size="small"
                      label={v.project_version_name || `V${v.version_number}.0`}
                      sx={{
                        fontWeight: 800, fontSize: '0.7rem', height: 22,
                        bgcolor: v.status === 'COMPLETED' ? 'rgba(34,197,94,0.12)'
                          : v.is_unlocked ? 'rgba(108,59,255,0.12)' : '#F1F5F9',
                        color: v.status === 'COMPLETED' ? '#16A34A' : v.is_unlocked ? '#6C3BFF' : '#94A3B8'
                      }}
                    />
                    {i < versionCards.length - 1 && <Typography sx={{ color: '#CBD5E1', fontWeight: 700 }}>→</Typography>}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
            <Grid container spacing={2.5}>
              {versionCards.map(v => {
                const locked = v.status === 'LOCKED';
                const completed = v.status === 'COMPLETED';
                const pct = versionProgressPct(v.status);
                const repo = v.version_repository_url || v.project_repository_url;
                const statusLabel = locked ? 'Locked'
                  : v.status === 'ACTIVE' ? 'In Progress'
                  : v.status === 'NOT_STARTED' ? 'Not Started'
                  : v.status === 'IN_PROGRESS' ? 'In Progress'
                  : v.status === 'UNDER_REVIEW' ? 'Under Review'
                  : 'Completed';
                return (
                  <Grid item xs={12} sm={6} lg={3} key={v.id}>
                    <Box sx={{
                      p: 2.5, height: '100%', borderRadius: '18px', bgcolor: '#fff',
                      border: '1px solid', display: 'flex', flexDirection: 'column',
                      borderColor: completed ? 'rgba(34,197,94,0.35)' : locked ? '#E2E8F0' : 'rgba(108,59,255,0.35)',
                      opacity: locked ? 0.6 : 1,
                      transition: '0.2s',
                      '&:hover': locked ? {} : { boxShadow: '0 10px 30px rgba(108,59,255,0.12)' }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Chip size="small" label={v.project_version_name || `V${v.version_number}.0`}
                          sx={{ fontWeight: 800, bgcolor: completed ? 'rgba(34,197,94,0.12)' : 'rgba(108,59,255,0.12)', color: completed ? '#16A34A' : '#6C3BFF' }} />
                        <Chip size="small" label={locked ? '🔒 Locked' : completed ? '✓ Completed' : statusLabel}
                          sx={{
                            ml: 'auto', fontWeight: 700, fontSize: '0.68rem',
                            bgcolor: completed ? 'rgba(34,197,94,0.12)' : locked ? '#F1F5F9' : 'rgba(245,158,11,0.12)',
                            color: completed ? '#16A34A' : locked ? '#94A3B8' : '#B45309'
                          }} />
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>
                        {v.project_name || 'Project'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748B', mb: 1 }}>
                        {v.preferred_role || 'Engineer'}{v.sprint_duration ? ` · ${v.sprint_duration}` : ''}
                        {v.default_team_size ? ` · ${v.default_team_size} members` : ''}
                      </Typography>
                      {(v.tech_stacks?.length || v.primary_language) && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                          {(v.tech_stacks || [v.primary_language]).filter(Boolean).slice(0, 3).map((t, i) => (
                            <Chip key={i} size="small" label={t} sx={{ fontSize: '0.62rem', height: 18, bgcolor: '#F8FAFC', color: '#64748B', fontWeight: 600 }} />
                          ))}
                        </Box>
                      )}
                      {/* Progress */}
                      <Box sx={{ mt: 'auto', pt: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700 }}>Progress</Typography>
                          <Typography variant="caption" sx={{ color: completed ? '#16A34A' : '#6C3BFF', fontWeight: 800 }}>{pct}%</Typography>
                        </Box>
                        <Box sx={{ height: 6, borderRadius: 3, bgcolor: '#F1F5F9', overflow: 'hidden', mb: 1.5 }}>
                          <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: completed ? '#22C55E' : '#6C3BFF' }} />
                        </Box>
                        {completed && v.completed_date && (
                          <Typography variant="caption" sx={{ display: 'block', color: '#16A34A', fontWeight: 600, mb: 1 }}>
                            Completed {new Date(v.completed_date).toLocaleDateString()}
                          </Typography>
                        )}
                        <Tooltip title={locked ? 'Complete previous version to unlock.' : ''} placement="top">
                          <span style={{ display: 'block' }}>
                            <Button
                              fullWidth
                              size="small"
                              variant="contained"
                              disabled={locked}
                              onClick={() => navigate(`/nextgen-builder/dashboard/project/${v.id}`)}
                              sx={{
                                borderRadius: '10px', textTransform: 'none', fontWeight: 700,
                                ...(locked
                                  ? { bgcolor: '#E2E8F0 !important', color: '#94A3B8 !important' }
                                  : { background: 'linear-gradient(135deg, #6C3BFF, #8B5CF6)', '&:hover': { background: 'linear-gradient(135deg, #5A2EE5, #7C3AED)' } })
                              }}
                            >
                              {locked ? `Available after V${v.display_order - 1} completion` : 'Open Dashboard'}
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* ── Metrics Row ── */}
        <motion.div variants={stagger} initial="initial" animate="animate">
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {[
              {
                icon: <AccountBalanceWalletRoundedIcon />,
                label: 'Wallet Balance',
                value: loading ? '' : `₹${(wallet.balance || 0).toLocaleString('en-IN')}`,
                subtitle: `Lifetime Earned: ₹${(wallet.totalEarnings || 0).toLocaleString('en-IN')}`,
                color: '#22C55E',
              },
              {
                icon: <PeopleRoundedIcon />,
                label: 'Total Referrals',
                value: loading ? '' : (referrals.total || 0),
                subtitle: `${referrals.successful || 0} Successful · ${referrals.pending || 0} Pending`,
                color: '#6C3BFF',
              },
              {
                icon: <StarRoundedIcon />,
                label: 'AI Review Score',
                value: loading ? '' : `${data?.aiCodeReviewScore || 0}/100`,
                subtitle: 'Based on latest code review',
                color: '#F59E0B',
              },
              {
                icon: <CalendarTodayRoundedIcon />,
                label: 'Attendance',
                value: loading ? '' : (data?.attendance || '0%'),
                subtitle: subscription.validTill
                  ? `Valid till ${new Date(subscription.validTill).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`
                  : 'No active subscription',
                color: '#06B6D4',
              },
            ].map((card, idx) => (
              <Grid item xs={12} sm={6} lg={3} key={idx} component={motion.div} variants={fadeUp}>
                <MetricCard {...card} loading={loading} />
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* ── Project Progress ── */}
        {(loading || subscription.isActive) && (
          <Paper
            elevation={0}
            component={motion.div}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            sx={{
              p: 2.5, borderRadius: '16px', mb: 3,
              background: CARD_BG, border: BORDER,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ color: '#9B6FFF', display: 'flex' }}>
                  <WorkRoundedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F172A', fontSize: '0.9rem' }}>
                  Project Progress
                </Typography>
              </Box>
              {loading ? (
                <Skeleton variant="rectangular" width={72} height={22} sx={{ borderRadius: 1 }} />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {data?.profile?.projectVersion && (
                    <Chip
                      label={`v${data.profile.projectVersion}`}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(108,59,255,0.1)',
                        color: '#6C3BFF',
                        border: '1px solid rgba(108,59,255,0.2)',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 22,
                      }}
                    />
                  )}
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#6C3BFF', fontSize: '1.1rem' }}>
                    {data?.projectProgress || 0}%
                  </Typography>
                </Box>
              )}
            </Box>

            {loading ? (
              <Skeleton variant="rectangular" height={10} sx={{ borderRadius: 99 }} />
            ) : (
              <LinearProgress
                variant="determinate"
                value={data?.projectProgress || 0}
                sx={{
                  height: 10, borderRadius: 99,
                  bgcolor: '#E2E8F0',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #6C3BFF 0%, #9B6FFF 100%)',
                  },
                }}
              />
            )}

            {!loading && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.25, px: 0.25 }}>
                <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                  {data?.profile?.projectName || 'No project assigned yet'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                  {data?.profile?.assignedTeam ? `Team: ${data.profile.assignedTeam}` : ''}
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* ── Bottom Grid ── */}
        <Grid container spacing={2.5}>
          {/* Referral Stats */}
          <Grid item xs={12} md={4}>
            <SectionCard
              title="Referral Statistics"
              icon={<CardGiftcardRoundedIcon />}
              loading={loading}
              minHeight={280}
              action={
                !loading && (
                  <Button
                    size="small"
                    endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={() => navigate('/nextgen-builder/refer')}
                    sx={{
                      color: '#6C3BFF',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      px: 1,
                      '&:hover': { bgcolor: 'rgba(108,59,255,0.08)' },
                    }}
                  >
                    Refer Now
                  </Button>
                )
              }
            >
              <Stack spacing={1.75}>
                {[
                  { label: 'Total Referrals', value: referrals.total || 0, color: '#6C3BFF', icon: <PeopleRoundedIcon sx={{ fontSize: 15 }} /> },
                  { label: 'Successful', value: referrals.successful || 0, color: '#22C55E', icon: <CheckCircleRoundedIcon sx={{ fontSize: 15 }} /> },
                  { label: 'Pending', value: referrals.pending || 0, color: '#F59E0B', icon: <HourglassEmptyRoundedIcon sx={{ fontSize: 15 }} /> },
                  { label: 'Wallet Earnings', value: `₹${(wallet.totalEarnings || 0).toLocaleString('en-IN')}`, color: '#22C55E', icon: <AccountBalanceWalletRoundedIcon sx={{ fontSize: 15 }} /> },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      p: 1.25, borderRadius: '10px',
                      background: '#F8FAFC',
                      border: '1px solid #E5E7EB',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#94A3B8' }}>
                      {item.icon}
                      <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500, fontSize: '0.82rem' }}>
                        {item.label}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: item.color, fontSize: '0.9rem' }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </SectionCard>
          </Grid>

          {/* Assigned Projects */}
          <Grid item xs={12} md={4}>
            <SectionCard title="Assigned Projects" icon={<WorkRoundedIcon />} loading={loading} minHeight={280}>
              {(data?.assignedProjects?.length || 0) === 0 ? (
                <EmptyState
                  icon={<WorkRoundedIcon />}
                  title="No Projects Assigned"
                  message="Subscribe to a plan to get your first project."
                  action={
                    !subscription.isActive && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<ShoppingCartRoundedIcon />}
                        onClick={() => navigate('/nextgen-builder/purchase')}
                        sx={{
                          background: 'linear-gradient(135deg, #6C3BFF, #8B5CF6)',
                          borderRadius: '10px',
                          fontWeight: 700,
                          '&:hover': { background: 'linear-gradient(135deg, #5020E0, #7C3AED)' },
                        }}
                      >
                        Browse Plans
                      </Button>
                    )
                  }
                />
              ) : (
                <List disablePadding>
                  {data.assignedProjects.map((p, i) => (
                    <ListItem
                      key={i}
                      disablePadding
                      sx={{
                        py: 1, px: 1.25, borderRadius: '10px', mb: 0.75,
                        background: '#F8FAFC',
                        border: '1px solid #E5E7EB',
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 36 }}>
                        <Box
                          sx={{
                            width: 28, height: 28, borderRadius: '8px',
                            background: 'linear-gradient(135deg, rgba(108,59,255,0.12), rgba(139,92,246,0.12))',
                            border: '1px solid rgba(108,59,255,0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#6C3BFF', fontWeight: 800, fontSize: '0.7rem',
                          }}
                        >
                          {(p.name || 'P').charAt(0).toUpperCase()}
                        </Box>
                      </ListItemAvatar>
                      <ListItemText
                        primary={p.name}
                        secondary={p.status}
                        primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600, color: '#0F172A' }}
                        secondaryTypographyProps={{ fontSize: '0.75rem', color: '#94A3B8' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </SectionCard>
          </Grid>

          {/* Announcements */}
          <Grid item xs={12} md={4}>
            <SectionCard title="Announcements" icon={<CampaignRoundedIcon />} loading={loading} minHeight={280}>
              {(data?.announcements?.length || 0) === 0 ? (
                <EmptyState
                  icon={<CampaignRoundedIcon />}
                  title="No Announcements"
                  message="Stay tuned for updates and announcements."
                />
              ) : (
                <List disablePadding>
                  {data.announcements.map((a, i) => (
                    <ListItem
                      key={i}
                      disablePadding
                      sx={{
                        py: 1.25, px: 1.25, borderRadius: '10px', mb: 0.75,
                        background: '#F8FAFC',
                        border: '1px solid #E5E7EB',
                        alignItems: 'flex-start', gap: 1,
                      }}
                    >
                      <FiberManualRecordIcon sx={{ fontSize: 8, color: '#6C3BFF', mt: 0.75, flexShrink: 0 }} />
                      <ListItemText
                        primary={a.title}
                        secondary={a.date}
                        primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600, color: '#0F172A', lineHeight: 1.4 }}
                        secondaryTypographyProps={{ fontSize: '0.72rem', color: '#94A3B8', mt: 0.25 }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </SectionCard>
          </Grid>
        </Grid>
      </Box>
    </motion.div>
  );
};

export default NextGenDashboard;